import { useEffect, useMemo, useRef } from 'react';
import { LICENSED_PRODUCTS } from '@atlassian/jira-common-util-get-tenant-context';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useProjectContext } from '@atlassian/jira-providers-project-context';
import { FREE_EDITION, PREMIUM_EDITION, STANDARD_EDITION } from '@atlassian/jira-shared-types';
import {
	useIsAdmin,
	useTenantContext,
	useAppEditions,
	useLicensedProducts,
} from '@atlassian/jira-tenant-context-controller';

export const useJwmAutomationDiscoveryOnBoardColumnsExperiment = (shouldFireExposure = false) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const event = createAnalyticsEvent({});
	event.context.push({ source: 'unknown', tags: ['measurement'] });

	const eligible = useRef(false);

	// Intentionally not firing exposure here because the user is not yet determined to be eligible.
	const [expConfig, fireExposure] = UNSAFE_noExposureExp('jwm_moon_automation_discovery_v2');
	const cohort = expConfig.get('cohort', 'not-enrolled');

	// User must be a valid user
	const { atlassianAccountId, cloudId, isAnonymous } = useTenantContext();
	const isValidUser = atlassianAccountId && cloudId && !isAnonymous;

	// User must have an Implicit Jwm License
	const licensedProducts = useLicensedProducts();
	const { software: softwareEdition } = useAppEditions();

	// User must be a site or project admin.
	const isAdmin = useIsAdmin();
	const projectContext = useProjectContext();

	const checkEligibility = useMemo(() => {
		const editions = [FREE_EDITION, STANDARD_EDITION, PREMIUM_EDITION];
		const hasImplicitJwmLicense =
			!licensedProducts?.[LICENSED_PRODUCTS.JIRA_CORE] ||
			(licensedProducts?.[LICENSED_PRODUCTS.JIRA_SOFTWARE] &&
				editions.some((edition) => edition === softwareEdition));

		const isAutomationAdmin = isAdmin || projectContext?.data?.isProjectAdmin || false;

		if (isValidUser && hasImplicitJwmLicense && isAutomationAdmin) {
			eligible.current = true;
			return cohort === 'variation';
		}

		return false;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [shouldFireExposure]);

	useEffect(() => {
		if (shouldFireExposure && eligible.current) {
			fireTrackAnalytics(event, 'feature exposed', {
				flagKey: 'jwm_moon_automation_discovery_v2',
				value: cohort,
			});
			fireExposure();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { shouldRenderExperiment: checkEligibility };
};
